/* ============================================================================
   mz-dawn-bridge.js — utilities the ported Dawn components (cart-drawer,
   details-modal search, predictive search) need but this theme doesn't define.
   Loaded in <head> (defer) BEFORE cart-drawer.js / details-modal.js /
   search-form.js so classes find these at define/upgrade time.

   Also owns two site-wide behaviors:
   • Rebinds the theme's header search triggers (.top-search-toggle) to the new
     Dawn-style search modal (details-modal.header__search).
   • mzEmit(): one custom-event helper so every ported feature announces itself
     (cart:updated, cart:item-added, cart-drawer:open/close, search:open/close,
     mbb:*, cart-dock:*) for apps/scripts to hook into.
   ========================================================================== */

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (elementToFocus) elementToFocus.focus();

  if (
    elementToFocus &&
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

/* The theme defines these too (theme.js), but that loads at the END of <body>;
   the ported components upgrade earlier, so guarantee them here. Same semantics. */
if (typeof window.debounce !== 'function') {
  window.debounce = function (fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };
}
if (typeof window.fetchConfig !== 'function') {
  window.fetchConfig = function (type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
  };
}

/* ---- Custom events on all fronts ------------------------------------------
   mzEmit('cart:updated', {...}) → document CustomEvent, bubbles, detail payload.
   Every ported feature calls this so storefront scripts and apps can listen:
     document.addEventListener('cart:item-added', (e) => console.log(e.detail))
   ------------------------------------------------------------------------- */
window.mzEmit = function (name, detail) {
  try {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  } catch (e) {}
};

/* ---- Search upgrade: route ALL search triggers to the new modal ----------
   The theme's header renders .top-search-toggle buttons wired (in theme.js) to
   the old popup/drawer search. Capture-phase interception opens the new
   Dawn-style predictive modal instead — theme handlers never fire. */
(function () {
  function searchModal() {
    return document.querySelector('details-modal.header__search');
  }
  document.addEventListener(
    'click',
    function (e) {
      var trigger = e.target.closest && e.target.closest('.top-search-toggle, [data-open-search-modal]');
      if (!trigger) return;
      var modal = searchModal();
      if (!modal) return; // modal not on page — let the theme handle it
      e.preventDefault();
      e.stopImmediatePropagation();
      if (typeof modal.openModal === 'function') {
        modal.openModal(trigger);
      } else {
        var details = modal.querySelector('details');
        if (details && !details.hasAttribute('open')) details.setAttribute('open', '');
        var input = modal.querySelector('input[type="search"]');
        if (input) setTimeout(function () { input.focus(); }, 60);
      }
    },
    true
  );
})();

/* ---- Out-of-stock last -----------------------------------------------------
   Push sold-out product cards ([data-mz-oos], stamped by product-item.liquid)
   to the end of their grid, on every listing (collections, search, tabs,
   recommendations). Toggle: Theme settings → TAOS Catalog. Runs at eval (before
   the theme initializes sliders) and again on AJAX grid updates. Server-side
   pagination is untouched — reordering happens within each rendered page. */
(function () {
  if (window.MZ_OOS_LAST === false) return;

  function cellFor(card) {
    var el = card;
    while (el.parentElement) {
      var p = el.parentElement;
      // HARD BOUNDARY: never climb past a section root or page container. A lone
      // OOS card in a sparse section once walked to page level, where "cells"
      // were whole sections — appendChild then moved the MAIN PRODUCT section to
      // the end of the page (PDP rendered as banner+combos, product at bottom).
      if (p === document.body || p.tagName === 'MAIN' ||
          (p.id && (p.id.indexOf('shopify-section-') === 0 || p.id === 'icartShopifyCartContent')) ||
          p.classList.contains('shopify-section')) return null;
      var cells = 0;
      for (var i = 0; i < p.children.length; i++) {
        var ch = p.children[i];
        if (ch.classList.contains('product-item') || ch.querySelector('.product-item')) cells++;
      }
      if (cells > 1) return { cell: el, grid: p };
      el = p;
    }
    return null;
  }

  function reorder(root) {
    var cards = (root || document).querySelectorAll('[data-mz-oos]');
    var byGrid = new Map();
    cards.forEach(function (card) {
      var loc = cellFor(card);
      if (!loc) return;
      // Carousels (Ready-to-Buy Combos etc.): slides CAN be reordered after
      // init as long as we call swiper.update() afterwards — except loop mode,
      // where Swiper's clone slides make DOM moves unsafe. Before this, OOS
      // combos sat mid-carousel while the grid pages demoted correctly.
      if (loc.grid.classList.contains('swiper-wrapper')) {
        var swEl = loc.grid.closest('.swiper-initialized, .swiper-container-initialized');
        if (swEl && swEl.swiper && swEl.swiper.params && swEl.swiper.params.loop) return;
      }
      if (!byGrid.has(loc.grid)) byGrid.set(loc.grid, []);
      byGrid.get(loc.grid).push(loc.cell);
    });
    byGrid.forEach(function (cells, grid) {
      // Already last? skip to avoid pointless DOM churn.
      var moved = false;
      cells.forEach(function (cell) {
        if (cell !== grid.lastElementChild) moved = true;
        grid.appendChild(cell);
      });
      if (moved) {
        var swHost = grid.closest('.swiper-initialized, .swiper-container-initialized');
        if (swHost && swHost.swiper && swHost.swiper.update) { try { swHost.swiper.update(); } catch (e) {} }
        if (window.mzEmit) mzEmit('catalog:oos-reordered', { count: cells.length });
      }
    });
  }

  // Initial pass: DOM is parsed when deferred scripts run, before theme sliders init.
  reorder();

  // Re-run after AJAX updates (filters, load-more, tab switches) — debounced.
  var t = null;
  var mo = new MutationObserver(function (muts) {
    /* PERF (Aug 2026): this used to re-run a full reorder after ANY node was
       added anywhere (chat widgets, popups, pixels, swiper clones fire this
       constantly). Now it only reacts when actual product cards arrive, and
       never while multi-pick owns the grid. Loosening this back makes the
       reorder run dozens of times per page life on app-heavy pages. */
    if (document.body.classList.contains('mzp-on')) return;
    for (var i = 0; i < muts.length; i++) {
      var m = muts[i];
      if (!m.addedNodes || !m.addedNodes.length) continue;
      for (var j = 0; j < m.addedNodes.length; j++) {
        var n = m.addedNodes[j];
        if (n.nodeType !== 1) continue;
        if ((n.classList && n.classList.contains('product-item')) || (n.querySelector && n.querySelector('.product-item'))) {
          clearTimeout(t);
          t = setTimeout(function () { reorder(); }, 120);
          return;
        }
      }
    }
  });
  if (document.body) mo.observe(document.body, { childList: true, subtree: true });
  else document.addEventListener('DOMContentLoaded', function () {
    mo.observe(document.body, { childList: true, subtree: true });
  });
})();
